import { BodyR } from '@pensionbee/honeypot-ui';
import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import { twMerge } from 'tailwind-merge';

type RadioGroupOption = {
  label: string;
  value: string;
  description?: string;
  helperOnSelected?: string;
};

type RadioGroupProps = {
  value: string;
  onChange: (value: string) => void;
  label: string;
  options: RadioGroupOption[];
  error?: string;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
};

export const RadioGroup = ({
  value,
  onChange,
  label,
  options,
  error,
  orientation = 'vertical',
  className,
}: RadioGroupProps) => {
  return (
    <div className="flex flex-col">
      <RadixRadioGroup.Root
        data-rc="RadioGroup"
        value={value}
        onValueChange={onChange}
        aria-label={label}
        className={twMerge(
          'flex w-full',
          orientation === 'horizontal' ? 'flex-row gap-16' : 'flex-col gap-28',
          className,
        )}
      >
        {options.map(
          ({ value: v, label: l, description, helperOnSelected }) => (
            <div key={`radio-${l}-${v}`} className="w-full">
              <RadixRadioGroup.Item
                value={v}
                aria-label={l}
                className={twMerge(
                  'flex items-center text-left gap-16 w-full px-16 py-20 rounded-[24px] cursor-pointer',
                  value === v ? 'bg-grey-5' : 'bg-grey-6',
                  error && 'aria-[invalid=true]:outline-red-100',
                )}
                aria-invalid={!!error}
              >
                <div
                  className={twMerge(
                    'relative size-20 shrink-0 rounded-full border-2 bg-background flex items-center justify-center',
                    value === v ? 'border-blue-100' : 'border-grey-3',
                  )}
                  data-error={!!error}
                >
                  <RadixRadioGroup.Indicator>
                    <svg
                      className="w-20 h-20 text-blue-100"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="6" />
                    </svg>
                  </RadixRadioGroup.Indicator>
                </div>
                <div className="flex flex-col gap-[10px]">
                  <span className="text-foreground font-bold">{l}</span>
                  {description && <BodyR>{description}</BodyR>}
                </div>
              </RadixRadioGroup.Item>
              {value === v && helperOnSelected && (
                <div className="mx-16 mt-12 text-foreground text-base">
                  {helperOnSelected}
                </div>
              )}
            </div>
          ),
        )}
      </RadixRadioGroup.Root>

      {error && (
        <div className="relative mt-8 rounded-4 bg-red-100 px-16 py-4 text-white text-sm before:absolute before:left-16 before:top-[-10px] before:border-[5px] before:border-b-red-100 before:content-['']">
          {error}
        </div>
      )}
    </div>
  );
};
